// OPT-HIST-02B — Inventario reciente de evaluaciones; no equivale a decisiones aplicadas.
import React, { useEffect, useMemo, useRef } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Typography,
} from '@mui/material';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useBackTarget, withReturnTo } from '../modules/pricingJourney/navigation';
import PageHeader, { pageFrameSx } from '../modules/pricingJourney/components/PageHeader';
import ProaDatePicker from '../components/Common/ProaDatePicker';
import EmptyState from '../modules/pricingJourney/components/EmptyState';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import {
  getOptimizationHistory,
  type OptimizationLogEntry,
} from '../services/optimizationService';
import { pricingNumberFormat } from '../modules/pricingJourney/formatNumber';

const surface = {
  bgcolor: '#111d2e',
  color: '#e2e8f0',
  border: '1px solid rgba(148,163,184,0.20)',
  borderRadius: 3,
  backgroundImage: 'none',
};

const numberText = (value: number | null | undefined, digits = 2): string =>
  value == null || !Number.isFinite(value)
    ? 'No disponible'
    : pricingNumberFormat({
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);

const dateText = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Fecha no disponible' : date.toLocaleString('es-ES');
};

const readObject = (value: unknown): Record<string, unknown> | null => {
  if (typeof value === 'string') {
    try { return readObject(JSON.parse(value) as unknown); } catch { return null; }
  }
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
};

const manualIntensity = (row: OptimizationLogEntry): number | null => {
  const value = readObject(row.full_input_payload)?.strategy_sensitivity;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100
    ? value : null;
};

const strategyText = (row: OptimizationLogEntry): string => {
  const profile = row.strategy_profile?.toLowerCase();
  if (profile === 'manual') {
    const intensity = manualIntensity(row);
    return intensity === null ? 'Manual · intensidad no registrada' : `Manual · ${numberText(intensity, intensity % 1 ? 1 : 0)} %`;
  }
  const labels: Record<string, string> = {
    conservador: 'Conservadora', equilibrado: 'Equilibrada', agresivo: 'Agresiva',
  };
  return labels[profile ?? ''] ?? 'Estrategia no disponible';
};

const OptimizationHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const back = useBackTarget('/optimization-center', 'Volver a Optimización');
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const scrollRestored = useRef(false);
  const search = params.get('q') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const requestedPage = Math.max(0, Number(params.get('page') ?? 0) || 0);
  const rowsPerPage = 10;

  const { data: rows = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['optimizationHistory'], queryFn: getOptimizationHistory, retry: 1,
  });

  useEffect(() => {
    const restoreY = (location.state as { restoreScrollY?: number } | null)?.restoreScrollY;
    if (!isLoading && !scrollRestored.current && typeof restoreY === 'number' && Number.isFinite(restoreY)) {
      scrollRestored.current = true;
      window.requestAnimationFrame(() => window.scrollTo({ top: restoreY, behavior: 'auto' }));
    }
  }, [isLoading, location.state]);

  const filtered = useMemo(() => rows.filter((row) => {
    const term = search.trim().toLowerCase();
    const matchesSearch = !term || [
      row.customer_id, row.customer_name, row.product_id, row.optimization_id,
      row.strategy_profile, strategyText(row),
    ].some((field) => String(field ?? '').toLowerCase().includes(term));
    const day = row.created_at.slice(0, 10); // Filtros por fecha UTC, tal como se almacena el registro.
    return matchesSearch && (!from || day >= from) && (!to || day <= to);
  }), [rows, search, from, to]);
  const page = Math.min(requestedPage, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1));
  const visible = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const hasFilters = Boolean(search || from || to);
  const completed = filtered.filter((row) => row.success).length;
  const manual = filtered.filter((row) => row.strategy_profile?.toLowerCase() === 'manual').length;

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');
    setParams(next, { replace: true });
  };

  const openEvaluation = (row: OptimizationLogEntry) => {
    if (!row.optimization_id) return;
    navigate(withReturnTo(`/optimization/evaluations/${encodeURIComponent(row.optimization_id)}`, `${location.pathname}${location.search}`), {
      state: { historyScrollY: window.scrollY },
    });
  };

  const exportFiltered = () => {
    const quote = (value: unknown) => {
      const text = String(value ?? '');
      const safe = /^[=+@-]/.test(text) ? `'${text}` : text;
      return `"${safe.replace(/"/g, '""')}"`;
    };
    const heading = ['Fecha UTC', 'Referencia de evaluación', 'Cliente', 'Producto', 'Estrategia e intensidad', 'Precio recomendado (moneda no indicada)', 'Resultado del cálculo'];
    const lines = [heading, ...filtered.map((row) => [
      row.created_at, row.optimization_id, row.customer_id, row.product_id,
      strategyText(row), row.suggested_price, row.success ? 'Cálculo registrado' : 'Sin resultado satisfactorio',
    ])];
    const csv = lines.map((line) => line.map(quote).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'historial_evaluaciones_filtrado.csv';
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return <Box sx={pageFrameSx}>
    <Stack spacing={2.5}>
      {/* PRC-032: cabecera común. «Volver» al origen (centro o Evaluación rápida); sin origen, al centro. */}
      <PageHeader
        eyebrow="Optimización"
        title="Historial de evaluaciones"
        description="Recomendaciones calculadas para tus clientes y productos; no son decisiones ni cambios de precio aplicados."
        back={{ label: back.label, onClick: back.go }}
        sx={{ mb: 0 }}
        actions={(
          <>
            <Button startIcon={<RefreshRoundedIcon />} onClick={() => void refetch()}
              variant="outlined" sx={{ color: '#d5e9fa', borderColor: '#42617c', textTransform: 'none' }}>Actualizar</Button>
            <Button startIcon={<DownloadRoundedIcon />} onClick={exportFiltered} disabled={filtered.length === 0}
              variant="contained" sx={{ bgcolor: '#67dafa', color: '#062035', fontWeight: 800, textTransform: 'none', '&:hover': { bgcolor: '#9deaff' } }}>Exportar</Button>
          </>
        )}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
        {[
          ['Evaluaciones en la selección', filtered.length, 'Sobre los registros recientes disponibles'],
          ['Cálculos registrados', completed, 'Estado técnico, no decisión comercial'],
          ['Estrategia manual', manual, 'Evaluaciones con ajuste personalizado'],
        ].map(([label, value, note]) => <Paper key={String(label)} sx={{ ...surface, px: 2.5, py: 2 }}>
          <Typography variant="caption" color="#95b7d2" fontWeight={750}>{label}</Typography>
          <Typography variant="h4" color="#f8fafc" fontWeight={850} sx={{ my: 0.3 }}>{value}</Typography>
          <Typography variant="caption" color="#8298ae">{note}</Typography>
        </Paper>)}
      </Box>

      <Paper sx={{ ...surface, p: { xs: 2, md: 2.5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.8 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <TuneRoundedIcon sx={{ color: '#7dd3fc', fontSize: 19 }} />
            <Typography color="#f8fafc" fontWeight={750}>Buscar y filtrar</Typography>
          </Stack>
          {hasFilters && <Button size="small" onClick={() => {
            // PRC-027: limpiar filtros conserva el origen.
            const kept = new URLSearchParams();
            const origin = params.get('returnTo');
            if (origin) kept.set('returnTo', origin);
            setParams(kept, { replace: true });
          }}>Limpiar filtros</Button>}
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,2fr) repeat(2,minmax(0,1fr))' }, gap: 1.5 }}>
          <TextField size="small" label="Cliente, producto, estrategia o referencia" value={search}
            onChange={(e) => updateFilter('q', e.target.value)} fullWidth />
          {/* PRC-032: selector de fechas común. */}
          <ProaDatePicker label="Desde (UTC)" value={from} onChange={(value) => updateFilter('from', value)} maxDate={to || null} />
          <ProaDatePicker label="Hasta (UTC)" value={to} onChange={(value) => updateFilter('to', value)} minDate={from || null} />
        </Box>
        <Typography variant="caption" color="#91acc4" sx={{ display: 'block', mt: 1.5 }}>
          Vista provisional de las últimas 50 ejecuciones. Los filtros actúan únicamente sobre estos registros. La consulta completa y la paginación por cursor se incorporarán posteriormente.
        </Typography>
      </Paper>

      {isError && <Alert severity="error" action={<Button onClick={() => void refetch()} color="inherit">Reintentar</Button>}>
        No se pudo cargar el historial.
      </Alert>}
      <Paper sx={{ ...surface, overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, py: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} color="#f8fafc">Evaluaciones recientes</Typography>
          <Chip size="small" variant="outlined" label={`${filtered.length} visibles en esta selección`}
            sx={{ color: '#b7daf1', borderColor: '#345672' }} />
        </Stack>
        <Divider sx={{ borderColor: '#2d4055' }} />
        <TableContainer>
          <Table sx={{ minWidth: 930 }}>
            <TableHead><TableRow sx={{ bgcolor: '#0c1829', '& th': { color: '#98acc1', borderColor: '#2b4055', fontWeight: 800, fontSize: 12, py: 1.8 } }}>
              <TableCell>FECHA</TableCell>
              <TableCell>CLIENTE · PRODUCTO</TableCell>
              <TableCell>CONFIGURACIÓN</TableCell>
              <TableCell align="right">PRECIO RECOMENDADO*</TableCell>
              <TableCell>ESTADO</TableCell>
              <TableCell align="right">EVALUACIÓN</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={26} sx={{ my: 4 }} /></TableCell></TableRow>}
              {!isLoading && visible.length === 0 && <TableRow><TableCell colSpan={6} sx={{ p: 2, border: 0 }}>
                {/* PRC-032: estado vacío común. */}
                <EmptyState icon={<SearchOffRoundedIcon />} title="No hay evaluaciones en esta selección"
                  text="Cambia la búsqueda o las fechas para ver más resultados." minHeight={180} />
              </TableCell></TableRow>}
              {!isLoading && visible.map((row) => <TableRow key={row.id} hover
                sx={{ '& td': { borderColor: '#26394e', color: '#e2e8f0', py: 1.55 }, '&:hover': { bgcolor: '#192c40' } }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" fontWeight={750}>{dateText(row.created_at)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={800} color="#d7ecff">{row.customer_name || row.customer_id || 'Cliente nuevo'}</Typography>
                  <Typography variant="caption" color="#8faac0">Producto: {row.product_id || 'Nuevo / no identificado'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={strategyText(row)}
                    sx={{ bgcolor: row.strategy_profile?.toLowerCase() === 'manual' ? '#23364f' : '#193549',
                      border: '1px solid #3c607d', color: '#d9f0ff', fontWeight: 750 }} />
                </TableCell>
                <TableCell align="right"><Typography variant="body1" fontWeight={850} color="#f8fafc">{numberText(row.suggested_price)}</Typography></TableCell>
                <TableCell><Chip size="small" color={row.success ? 'success' : 'warning'}
                  label={row.success ? 'Cálculo registrado' : 'Sin resultado satisfactorio'} /></TableCell>
                <TableCell align="right"><Button size="small" endIcon={<ArrowOutwardRoundedIcon fontSize="small" />}
                  disabled={!row.optimization_id}
                  // PRC-032: enlace real; con un clic normal conserva la posición del historial.
                  component={RouterLink}
                  to={withReturnTo(`/optimization/evaluations/${encodeURIComponent(row.optimization_id ?? '')}`, `${location.pathname}${location.search}`)}
                  onClick={(event: React.MouseEvent) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                    event.preventDefault();
                    openEvaluation(row);
                  }}
                  sx={{ color: '#7ddfff', fontWeight: 800 }}>Ver ficha</Button></TableCell>
              </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]} onPageChange={(_e, nextPage) => {
            const next = new URLSearchParams(params);
            if (nextPage) next.set('page', String(nextPage)); else next.delete('page');
            setParams(next);
          }} sx={{ color: '#d0e0ee', borderTop: '1px solid #2a4054' }} />
      </Paper>
      <Typography variant="caption" color="#91a8bd">
        * Valores conservados sin símbolo monetario: aún no existe una moneda contractual disponible para esta ficha. Un precio recomendado no es un precio aplicado.
      </Typography>
    </Stack>
  </Box>;
};

export default OptimizationHistoryPage;
